import { NumberUtils } from '.';
import { DealRequestAllocation, StructureTranches, Tier1BankTypes } from '../models';

export function getTier1BankTypeValue(tier1?: Tier1BankTypes): number | null {
	switch (tier1) {
		case 'Commercial':
			return 10;
		case 'NonBank':
			return 20;
		default:
			return 99;
	}
}

export function getStructureTrancheValue(tranche?: StructureTranches): number | null {
	switch (tranche) {
		case 'SeniorCashFlow':
			return 1;
		case 'JuniorCashFlow':
			return 2;
		case 'VentureDebt':
			return 3;
		case 'Equity':
			return 4;
		case 'SeniorRealEstate':
			return 5;
		case 'JuniorRealEstate':
			return 6;
		case 'Sba7aRealEstate':
			return 7;
		case 'Sba7aNonRealEstate':
			return 8;
		case 'Sba504Senior':
			return 9;
		case 'Sba504Debenture':
			return 10;
		case 'UsdaBiRealEstate':
			return 11;
		case 'UsdaBiEquipment':
			return 12;
		case 'UsdaBiWorkingCapital':
			return 13;
		case 'AssetBasedWorkingCapitalAccountsReceivable':
			return 14;
		case 'AssetBasedWorkingCapitalInventory':
			return 15;
		case 'AssetBasedEquipmentMachinery':
			return 16;
		case 'AssetBasedEquipmentVehicles':
			return 17;
		case 'AssetBasedEquipmentMarine':
			return 18;
		case 'AssetBasedEquipmentHelicopters':
			return 19;
		case 'AssetBasedEquipmentAircraft':
			return 20;
		case 'AssetBasedIntangibleIntellectualProperty':
			return 21;
		case 'AssetBasedIntangibleRoyalty':
			return 22;
		case 'PoFinancing':
			return 24;
		case 'Factoring':
			return 25;
		case 'RecurringRevenue':
			return 26;
		case 'InventoryOnly':
			return 27;
		case 'LenderFinance':
			return 28;
		case 'FloorplanFinance':
			return 29;
		case 'AssetBasedWorkingCapitalArPlusInventory':
			return 30;
		case 'BridgeLoan':
			return 31;
	}

	return null;
}

export function isTrancheSelectedPreference(
	allocation?: DealRequestAllocation,
	trancheType?: StructureTranches,
	tier1?: Tier1BankTypes
): boolean {
	if (allocation == null || trancheType == null || tier1 == null) return false;

	if (tier1 == 'Commercial') {
		if (trancheType == 'SeniorCashFlow' || trancheType == 'JuniorCashFlow') {
			return allocation.cashFlowBankPreference ?? false;
		}

		if (
			trancheType == 'AssetBasedWorkingCapitalAccountsReceivable' ||
			trancheType == 'AssetBasedWorkingCapitalInventory'
		) {
			return allocation.assetBasedArInvBankPreference ?? false;
		}

		if (
			trancheType == 'AssetBasedEquipmentAircraft' ||
			trancheType == 'AssetBasedEquipmentHelicopters' ||
			trancheType == 'AssetBasedEquipmentMachinery' ||
			trancheType == 'AssetBasedEquipmentMarine' ||
			trancheType == 'AssetBasedEquipmentVehicles' ||
			trancheType == 'SeniorRealEstate' ||
			trancheType == 'JuniorRealEstate'
		) {
			return allocation.assetBasedFixedAssetsBankPreference ?? false;
		}

		if (
			trancheType == 'UsdaBiEquipment' ||
			trancheType == 'UsdaBiRealEstate' ||
			trancheType == 'UsdaBiWorkingCapital'
		) {
			return allocation.largeGovernmentPreference ?? false;
		}

		if (trancheType == 'VentureDebt' || trancheType == 'RecurringRevenue') {
			return allocation.ventureDebtPreference ?? false;
		}

		if (
			trancheType == 'Sba7aRealEstate' ||
			trancheType == 'Sba7aNonRealEstate' ||
			trancheType == 'Sba504Senior' ||
			trancheType == 'Sba504Debenture'
		) {
			return allocation.smallGovernmentPreference ?? false;
		}

		if (trancheType == 'LenderFinance') {
			return allocation.lenderFinanceBankPreference ?? false;
		}

		if (trancheType == 'FloorplanFinance') {
			return allocation.floorplanFinanceBankPreference ?? false;
		}
	} else if (tier1 == 'NonBank') {
		if (trancheType == 'SeniorCashFlow') {
			return allocation.cashFlowNonBankPreference ?? false;
		}

		if (trancheType == 'JuniorCashFlow') {
			return allocation.juniorCashFlowNonBankPreference ?? false;
		}

		if (trancheType == 'BridgeLoan') {
			return allocation.bridgeLoanNonBankPreference ?? false;
		}

		if (
			trancheType == 'AssetBasedWorkingCapitalAccountsReceivable' ||
			trancheType == 'AssetBasedWorkingCapitalInventory'
		) {
			return allocation.assetBasedArInvNonBankPreference ?? false;
		}

		if (
			trancheType == 'AssetBasedEquipmentAircraft' ||
			trancheType == 'AssetBasedEquipmentHelicopters' ||
			trancheType == 'AssetBasedEquipmentMachinery' ||
			trancheType == 'AssetBasedEquipmentMarine' ||
			trancheType == 'AssetBasedEquipmentVehicles' ||
			trancheType == 'SeniorRealEstate' ||
			trancheType == 'JuniorRealEstate'
		) {
			return allocation.assetBasedFixedAssetsNonBankPreference ?? false;
		}

		if (trancheType == 'VentureDebt' || trancheType == 'RecurringRevenue') {
			return allocation.ventureBankNonBankPreference ?? false;
		}

		if (trancheType == 'PoFinancing') {
			return allocation.poFinancingPreference ?? false;
		}

		if (trancheType == 'Factoring') {
			return allocation.factoringPreference ?? false;
		}

		if (trancheType == 'InventoryOnly') {
			return allocation.inventoryOnlyPreference ?? false;
		}

		if (trancheType == 'LenderFinance') {
			return allocation.lenderFinanceNonBankPreference ?? false;
		}

		if (trancheType == 'FloorplanFinance') {
			return allocation.floorplanFinanceNonBankPreference ?? false;
		}
	}

	return false;
}

export function isTranchePreferenceSelectedByCerebro(
	allocation?: DealRequestAllocation,
	trancheType?: StructureTranches,
	tier1?: Tier1BankTypes
): boolean {
	if (allocation == null || trancheType == null || tier1 == null) return false;

	if (tier1 == 'Commercial') {
		if (trancheType == 'SeniorCashFlow' || trancheType == 'JuniorCashFlow') {
			return allocation.cashFlowBankPreferenceSelected ?? false;
		}

		if (trancheType == 'AssetBasedWorkingCapitalArPlusInventory') {
			return allocation.assetBasedArInvBankPreferenceSelected ?? false;
		}

		if (
			trancheType == 'AssetBasedEquipmentAircraft' ||
			trancheType == 'AssetBasedEquipmentHelicopters' ||
			trancheType == 'AssetBasedEquipmentMachinery' ||
			trancheType == 'AssetBasedEquipmentMarine' ||
			trancheType == 'AssetBasedEquipmentVehicles' ||
			trancheType == 'SeniorRealEstate' ||
			trancheType == 'JuniorRealEstate'
		) {
			return allocation.assetBasedFixedAssetsBankPreferenceSelected ?? false;
		}

		if (
			trancheType == 'UsdaBiEquipment' ||
			trancheType == 'UsdaBiRealEstate' ||
			trancheType == 'UsdaBiWorkingCapital'
		) {
			return allocation.largeGovernmentPreferenceSelected ?? false;
		}

		if (trancheType == 'VentureDebt' || trancheType == 'RecurringRevenue') {
			return allocation.ventureDebtPreferenceSelected ?? false;
		}

		if (
			trancheType == 'Sba7aRealEstate' ||
			trancheType == 'Sba7aNonRealEstate' ||
			trancheType == 'Sba504Senior' ||
			trancheType == 'Sba504Debenture'
		) {
			return allocation.smallGovernmentPreferenceSelected ?? false;
		}

		if (trancheType == 'LenderFinance') {
			return allocation.lenderFinanceBankPreferenceSelected ?? false;
		}

		if (trancheType == 'FloorplanFinance') {
			return allocation.floorplanFinanceBankPreferenceSelected ?? false;
		}
	} else if (tier1 == 'NonBank') {
		if (trancheType == 'SeniorCashFlow') {
			return allocation.cashFlowNonBankPreferenceSelected ?? false;
		}

		if (trancheType == 'JuniorCashFlow') {
			return allocation.juniorCashFlowNonBankPreferenceSelected ?? false;
		}

		if (trancheType == 'BridgeLoan') {
			return allocation.bridgeLoanNonBankPreferenceSelected ?? false;
		}

		if (trancheType == 'AssetBasedWorkingCapitalArPlusInventory') {
			return allocation.assetBasedArInvNonBankPreferenceSelected ?? false;
		}

		if (
			trancheType == 'AssetBasedEquipmentAircraft' ||
			trancheType == 'AssetBasedEquipmentHelicopters' ||
			trancheType == 'AssetBasedEquipmentMachinery' ||
			trancheType == 'AssetBasedEquipmentMarine' ||
			trancheType == 'AssetBasedEquipmentVehicles' ||
			trancheType == 'SeniorRealEstate' ||
			trancheType == 'JuniorRealEstate'
		) {
			return allocation.assetBasedFixedAssetsNonBankPreferenceSelected ?? false;
		}

		if (trancheType == 'VentureDebt' || trancheType == 'RecurringRevenue') {
			return allocation.ventureBankNonBankPreferenceSelected ?? false;
		}

		if (trancheType == 'PoFinancing') {
			return allocation.poFinancingPreferenceSelected ?? false;
		}

		if (trancheType == 'Factoring') {
			return allocation.factoringPreferenceSelected ?? false;
		}

		if (trancheType == 'InventoryOnly') {
			return allocation.inventoryOnlyPreferenceSelected ?? false;
		}

		if (trancheType == 'LenderFinance') {
			return allocation.lenderFinanceNonBankPreferenceSelected ?? false;
		}

		if (trancheType == 'FloorplanFinance') {
			return allocation.floorplanFinanceNonBankPreferenceSelected ?? false;
		}
	}

	return false;
}
